import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ShoppingCart, Award, Shield, TrendingDown, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { useCart } from '../context/CartContext';
import { productsAPI } from '../services/api';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SocialShareButtons from '../components/SocialShareButtons';
import MetaTags from '../components/MetaTags';
import { useToast } from '../hooks/use-toast';

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { toast } = useToast();
  const [quantity, setQuantity] = useState(1);
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProduct();
  }, [id]);

  const fetchProduct = async () => {
    setLoading(true);
    try {
      const response = await productsAPI.getById(id);
      setProduct(response.data);
    } catch (error) {
      console.error('Error fetching product:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="container mx-auto px-4 py-20 text-center">
          <p className="text-gray-600">Loading product...</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="container mx-auto px-4 py-20 text-center">
          <h1 className="text-2xl font-bold">Product not found</h1>
          <Button className="mt-4" onClick={() => navigate('/products')}>Back to Products</Button>
        </div>
        <Footer />
      </div>
    );
  }

  const handleAddToCart = () => {
    addToCart(product, quantity);
    toast({
      title: "Added to cart",
      description: `${product.name} has been added to your cart.`,
    });
  };

  const savingsPercentage = Math.round((1 - product.price / product.originalPrice) * 100);
  const totalSavings = product.originalPrice - product.price;
  const productUrl = window.location.href;
  const shareTitle = `${product.name} - Only ₹${product.price.toLocaleString()} | Youseful.shop`;
  const shareDescription = `${product.description} Save ${savingsPercentage}% - Direct from factory!`;

  return (
    <div className="min-h-screen bg-white">
      <MetaTags 
        title={shareTitle}
        description={shareDescription}
        image={product.image}
        url={productUrl}
      />
      <Header />

      <div className="container mx-auto px-4 py-8">
        <div className="grid md:grid-cols-2 gap-12">
          {/* Product Image */}
          <div>
            <div className="aspect-square rounded-2xl overflow-hidden bg-gray-100">
              <img
                src={product.image}
                alt={product.name}
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          {/* Product Info */}
          <div className="space-y-6">
            <div>
              <div className="text-sm text-gray-500 mb-2">{product.category}</div>
              <h1 className="text-4xl font-bold mb-2">{product.name}</h1>
              <div className="flex items-center space-x-4 mb-4">
                <div className="flex items-center">
                  <Star className="h-5 w-5 text-yellow-500 fill-yellow-500" />
                  <span className="ml-1 font-semibold">{product.rating}</span>
                  <span className="ml-1 text-gray-500">({product.reviews} reviews)</span>
                </div>
              </div>
              <div className="flex items-center space-x-2 text-sm text-gray-600">
                <Award className="h-4 w-4" />
                <span>Designed by {product.designer}</span>
              </div>
            </div>

            <div className="border-t border-b py-4">
              <div className="mb-3">
                <div className="text-sm text-gray-600 mb-1">Our Price</div>
                <div className="text-4xl font-bold text-gray-900">₹{product.price.toLocaleString()}</div>
              </div>
              <div className="flex items-center gap-4 flex-wrap">
                <div className="text-sm text-gray-600">
                  Similar Products in Market: <span className="line-through font-semibold">₹{product.originalPrice.toLocaleString()}</span>
                </div>
                <div className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-bold">
                  {savingsPercentage}% Less than Market
                </div>
              </div>
              <p className="text-green-600 font-semibold mt-2">
                Save ₹{totalSavings.toLocaleString()} compared to market price!
              </p>
            </div>

            <div>
              <h3 className="font-bold mb-2">Description</h3>
              <p className="text-gray-600">{product.description}</p>
            </div>

            {/* Cost Breakdown */}
            <Card className="p-6 bg-gray-50">
              <h3 className="font-bold mb-4 flex items-center">
                <TrendingDown className="mr-2 h-5 w-5 text-red-600" />
                Complete Cost Transparency
              </h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">Manufacturing</span>
                  <span className="font-semibold">₹{product.costBreakdown.manufacturing}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Quality Testing</span>
                  <span className="font-semibold">₹{product.costBreakdown.quality}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Logistics</span>
                  <span className="font-semibold">₹{product.costBreakdown.logistics}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Platform Margin</span>
                  <span className="font-semibold">₹{product.costBreakdown.platform}</span>
                </div>
                <div className="border-t pt-2 flex justify-between font-bold">
                  <span>Total</span>
                  <span className="text-red-600">₹{product.price}</span>
                </div>
              </div>
            </Card>

            {/* Quality Badges */}
            <div className="flex flex-wrap gap-4">
              <div className="flex items-center space-x-2 text-sm">
                <Shield className="h-5 w-5 text-green-600" />
                <span>Lab Tested</span>
              </div>
              <div className="flex items-center space-x-2 text-sm">
                <Shield className="h-5 w-5 text-green-600" />
                <span>Warranty Included</span>
              </div>
              <div className="flex items-center space-x-2 text-sm">
                <Award className="h-5 w-5 text-green-600" />
                <span>Designer Product</span>
              </div>
            </div>

            {/* Add to Cart */}
            <div className="space-y-4">
              {/* Social Sharing */}
              <div className="border-t pt-4">
                <label className="font-semibold mb-3 block text-sm text-gray-700">Share this product</label>
                <SocialShareButtons 
                  url={productUrl}
                  title={shareTitle}
                  description={shareDescription}
                  image={product.image}
                />
              </div>

              <div className="flex items-center space-x-4">
                <label className="font-semibold">Quantity:</label>
                <div className="flex items-center border rounded-lg">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="px-4 py-2 hover:bg-gray-100"
                  >
                    -
                  </button>
                  <span className="px-6 py-2 border-x">{quantity}</span>
                  <button
                    onClick={() => setQuantity(quantity + 1)}
                    className="px-4 py-2 hover:bg-gray-100"
                  >
                    +
                  </button>
                </div>
              </div>
              <div className="flex space-x-4">
                <Button
                  size="lg"
                  className="flex-1 bg-red-600 hover:bg-red-700"
                  onClick={handleAddToCart}
                >
                  <ShoppingCart className="mr-2 h-5 w-5" />
                  Add to Cart
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    handleAddToCart();
                    navigate('/cart');
                  }}
                >
                  Buy Now
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ProductDetail;
